import { useState, useEffect } from 'react';
import { Settings, Database, Users, FileText, Trash2, RefreshCw, AlertTriangle, CheckCircle, XCircle, MessageCircle, History } from 'lucide-react';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import { VectorOverviewPanel } from './ModernVectorOverview';

export function ModernAdminPanel({ useHistory = true, onHistoryChange }) {
  console.log("✅ AdminPanel is gerenderd");
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalFiles: 0,
    totalChunks: 0,
    storageUsed: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [operations, setOperations] = useState({
    reindexing: false,
    deleting: false,
    cleaning: false,
    backup: false
  });

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      // Note: You'll need to implement these endpoints in your backend
      const response = await fetch('/api/admin/stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching admin stats:', err);
      toast.error('Fout bij het ophalen van statistieken');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReindex = async () => {
    setOperations(prev => ({ ...prev, reindexing: true }));
    try {
      const response = await fetch('/api/admin/reindex', { method: 'POST' });
      if (response.ok) {
        toast.success('Vector database succesvol geherindexeerd');
        fetchStats();
      } else {
        throw new Error('Reindexering mislukt');
      }
    } catch (err) {
      toast.error('Fout bij herindexering');
    } finally {
      setOperations(prev => ({ ...prev, reindexing: false }));
    }
  };

  const handleDeletedata = async () => {
    setOperations(prev => ({ ...prev, deleting: true }));
    try {
      const response = await fetch('/api/admin/deletedata', { method: 'POST' });
      if (response.ok) {
        toast.success('Bestanden en vector database succesvol gewist');
        fetchStats();
      } else {
        throw new Error('Verwijderen van data mislukt');
      }
    } catch (err) {
      toast.error('Fout bij data verwijderen');
    } finally {
      setOperations(prev => ({ ...prev, deleting: false }));
    }
  };

  const handleCleanup = async () => {
    setOperations(prev => ({ ...prev, cleaning: true }));
    try {
      const response = await fetch('/api/admin/cleanup', { method: 'POST' });
      if (response.ok) {
        toast.success('Database opgeschoond');
        fetchStats();
      } else {
        throw new Error('Opschoning mislukt');
      }
    } catch (err) {
      toast.error('Fout bij database opschoning');
    } finally {
      setOperations(prev => ({ ...prev, cleaning: false }));
    }
  };

  const handleBackup = async () => {
    setOperations(prev => ({ ...prev, backup: true }));
    try {
      const response = await fetch('/api/admin/backup', { method: 'POST' });
      if (response.ok) {
        toast.success('Backup succesvol aangemaakt');
      } else {
        throw new Error('Backup mislukt');
      }
    } catch (err) {
      toast.error('Fout bij het maken van backup');
    } finally {
      setOperations(prev => ({ ...prev, backup: false }));
    }
  };

  return (
    <div className="h-full bg-white dark:bg-dark-900 overflow-y-auto scrollbar-thin">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Settings className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Admin Panel
          </h1>
        </div>

        {/* Chat Settings */}
        <div className="bg-white dark:bg-dark-800 rounded-xl p-6 border border-gray-200 dark:border-dark-700 shadow-sm mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Chat Instellingen
          </h2>
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors">
              <input
                type="checkbox"
                checked={useHistory}
                onChange={(e) => onHistoryChange?.(e.target.checked)}
                className="w-4 h-4 text-primary-600 bg-gray-100 dark:bg-dark-700 border-gray-300 dark:border-dark-600 rounded focus:ring-primary-500 dark:focus:ring-primary-600"
              />
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Chat Geschiedenis Gebruiken
                </span>
              </div>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 ml-7">
              Wanneer ingeschakeld onthoudt de AI eerdere berichten in het gesprek voor betere context.
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={<Users className="h-6 w-6" />}
            title="Gebruikers"
            value={stats.totalUsers}
            loading={isLoading}
            color="blue"
          />
          <StatCard
            icon={<FileText className="h-6 w-6" />}
            title="Bestanden"
            value={stats.totalFiles}
            loading={isLoading}
            color="green"
          />
          <StatCard
            icon={<Database className="h-6 w-6" />}
            title="Vector Chunks"
            value={stats.totalChunks}
            loading={isLoading}
            color="purple"
          />
          <StatCard
            icon={<Database className="h-6 w-6" />}
            title="Opslag"
            value={formatBytes(stats.storageUsed)}
            loading={isLoading}
            color="orange"
            isBytes
          />
        </div>

        {/* Actions */}
        <div className="space-y-6">
          {/* Database Operations */}
          <div className="bg-gray-50 dark:bg-dark-800/50 rounded-xl p-6 border border-gray-200 dark:border-dark-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Database className="h-5 w-5" />
              Database Operaties
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* <ActionButton
                icon={<RefreshCw className="h-5 w-5" />}
                title="Herindexeren"
                description="Vector database opnieuw indexeren"
                onClick={handleReindex}
                loading={operations.reindexing}
                variant="primary"
              /> */}
              <ActionButton
                icon={<RefreshCw className="h-5 w-5" />}
                title="Verwijder bestanden"
                description="Geuploade bestanden en vectordatabase verwijderen"
                onClick={handleDeletedata}
                loading={operations.deleting}
                variant="primary"
              />
              <ActionButton
                icon={<Trash2 className="h-5 w-5" />}
                title="Database Opschonen"
                description="Verwijder ongebruikte chatdata"
                onClick={handleCleanup}
                loading={operations.cleaning}
                variant="warning"
              />
              <ActionButton
                icon={<Database className="h-5 w-5" />}
                title="Backup Maken"
                description="Database backup aanmaken"
                onClick={handleBackup}
                loading={operations.backup}
                variant="secondary"
              />
            </div>
          </div>

          {/* System Status */}
          <div className="bg-gray-50 dark:bg-dark-800/50 rounded-xl p-6 border border-gray-200 dark:border-dark-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Systeem Status
            </h2>
            <div className="space-y-4">
              <StatusItem
                label="Database Verbinding"
                status={stats.totalUsers >= 0 ? "healthy" : "error"}
                description={`${stats.totalUsers} gebruikers in database`}
              />
              <StatusItem
                label="Vector Store"
                status={stats.totalChunks > 0 ? "healthy" : "warning"}
                description={`${stats.totalChunks} chunks beschikbaar`}
              />
              <StatusItem
                label="File Storage"
                status={stats.totalFiles > 0 ? "healthy" : "warning"}
                description={`${stats.totalFiles} bestanden • ${formatBytes(stats.storageUsed)}`}
              />
            </div>
          </div>

          {/* Vector Database Overzicht */}
          <VectorOverviewPanel />

          {/* Recent Activity */}
          <div className="bg-gray-50 dark:bg-dark-800/50 rounded-xl p-6 border border-gray-200 dark:border-dark-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Recente Activiteit
            </h2>
            <div className="text-center py-8">
              <div className="text-gray-400 dark:text-gray-500 text-sm">
                Activiteitenlogboek wordt momenteel ontwikkeld
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, title, value, loading, color, isBytes = false }) {
  const colorClasses = {
    blue: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30',
    green: 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30',
    purple: 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30',
    orange: 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30',
  };

  return (
    <div className="bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            {title}
          </p>
          {loading ? (
            <div className="h-8 w-16 bg-gray-200 dark:bg-dark-700 rounded animate-pulse"></div>
          ) : (
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {isBytes ? value : value.toLocaleString()}
            </p>
          )}
        </div>
        <div className={clsx('p-3 rounded-lg', colorClasses[color])}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function ActionButton({ icon, title, description, onClick, loading, variant }) {
  const variantClasses = {
    primary: 'bg-primary-600 hover:bg-primary-700 text-white',
    secondary: 'bg-gray-600 hover:bg-gray-700 text-white',
    warning: 'bg-yellow-600 hover:bg-yellow-700 text-white'
  };

  return (
    <div className="bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg p-4">
      <h3 className="font-medium text-gray-900 dark:text-white mb-2">
        {title}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        {description}
      </p>
      <button
        onClick={onClick}
        disabled={loading}
        className={clsx(
          'w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium',
          variantClasses[variant],
          loading && 'opacity-50 cursor-not-allowed'
        )}
      >
        {loading ? (
          <RefreshCw className="h-4 w-4 animate-spin" />
        ) : (
          icon
        )}
        {loading ? 'Bezig...' : 'Uitvoeren'}
      </button>
    </div>
  );
}

function StatusItem({ label, status, description }) {
  const statusConfig = {
    healthy: {
      icon: <CheckCircle className="h-5 w-5 text-green-500" />,
      color: 'text-green-600 dark:text-green-400'
    },
    warning: {
      icon: <AlertTriangle className="h-5 w-5 text-yellow-500" />,
      color: 'text-yellow-600 dark:text-yellow-400'
    },
    error: {
      icon: <XCircle className="h-5 w-5 text-red-500" />,
      color: 'text-red-600 dark:text-red-400'
    }
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-dark-700 last:border-0">
      <div className="flex items-center gap-3">
        {statusConfig[status].icon}
        <div>
          <p className="font-medium text-gray-900 dark:text-white">
            {label}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {description}
          </p>
        </div>
      </div>
      <span className={clsx('text-sm font-medium capitalize', statusConfig[status].color)}>
        {status === 'healthy' ? 'Gezond' : status === 'warning' ? 'Waarschuwing' : 'Fout'}
      </span>
    </div>
  );
}

function ActivityItem({ action, user, timestamp, type }) {
  const typeConfig = {
    upload: { icon: <FileText className="h-4 w-4" />, color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' },
    chat: { icon: <Users className="h-4 w-4" />, color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' },
    system: { icon: <Settings className="h-4 w-4" />, color: 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400' }
  };

  return (
    <div className="flex items-center gap-3 py-2">
      <div className={clsx('p-2 rounded-lg', typeConfig[type].color)}>
        {typeConfig[type].icon}
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {action}
        </p>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          {user} • {timestamp}
        </p>
      </div>
    </div>
  );
}
